// @flow

import React, { useEffect, useRef, useState } from "react"
import { injectIntl } from "react-intl"
import { observer } from "mobx-react-lite"
import styled from "styled-components"
import WideSpinner from "../../shared/components/WideSpinner"
import { Row } from "antd"
import { DefaultButton } from "@cabonline/shared/components/buttons"
import OrderSearchStore from "shared/stores/OrderSearchStore"
import { messageError } from "shared/utils/errors"

const FormRow = styled(Row)`
  width: 100%;
`

const FormContainer = styled(Row)`
  position: relative;
  display: inline-block;
  margin-top: 8px;
  margin-bottom: 8px;
  width: 100%;
`

const ConfirmationBox = styled.div`
  position: relative;
  top: 100%;
  left: 0;
  width: 100%;
  border: 1px solid rgba(0, 0, 0, 0.04);
  border-radius: 6px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.25);
  background: #ffffff;
  padding: 10px;
`

const ButtonWrapper = styled.div`
  .Button-defaultButton {
    border: none;
  }
`

const OrderHandlePanel = props => {
  const {
    intl: { formatMessage }
  } = props

  const { detailedOrder } = OrderSearchStore

  const node = useRef()

  const [loading, setLoading] = useState(false)
  const [completeConfirmationOpen, setCompleteConfirmationOpen] = useState(
    false
  )
  const [cancelConfirmationOpen, setCancelConfirmationOpen] = useState(false)

  const completeOrder = (orderId: string) => {
    setLoading(true)
    OrderSearchStore.adminCompleteOrder(orderId).catch(e => {
      setLoading(false)
      messageError("order-complete", e)
    })
  }

  const cancelOrder = (orderId: string) => {
    setLoading(true)
    OrderSearchStore.adminCancelOrder(orderId).catch(e => {
      setLoading(false)
      messageError("order-cancel", e)
    })
  }

  // To close confirmation boxes on a click outside the component
  const handleClickOutside = (event: Event) => {
    if (
      node.current != null &&
      event.target instanceof Node &&
      node.current.contains(event.target)
    ) {
      // inside click
      return
    }
    // outside click
    setCompleteConfirmationOpen(false)
    setCancelConfirmationOpen(false)
  }

  const openCompleteConfirmation = () => {
    setCompleteConfirmationOpen(true)
    setCancelConfirmationOpen(false)
  }

  const openCancelConfirmation = () => {
    setCompleteConfirmationOpen(false)
    setCancelConfirmationOpen(true)
  }

  useEffect(() => {
    if (completeConfirmationOpen || cancelConfirmationOpen) {
      document.addEventListener("mousedown", handleClickOutside)
    } else {
      document.removeEventListener("mousedown", handleClickOutside)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [completeConfirmationOpen, cancelConfirmationOpen])

  return (
    <div ref={node}>
      <h2>
        {formatMessage({
          id: "apps.order-search.title.unhandled"
        })}
      </h2>
      {loading ? (
        <WideSpinner />
      ) : (
        <div>
          <FormContainer>
            {!completeConfirmationOpen && (
              <ButtonWrapper>
                <DefaultButton
                  onClick={openCompleteConfirmation}
                  style={{
                    backgroundColor: "#00aa10",
                    color: "white",
                    width: "100%",
                    maxWidth: "500px"
                  }}
                >
                  {formatMessage({
                    id: "apps.order-search.button.mark.complete"
                  })}
                </DefaultButton>
              </ButtonWrapper>
            )}
            {completeConfirmationOpen && (
              <ConfirmationBox>
                <FormRow style={{ display: "flex", justifyContent: "center" }}>
                  <h3>
                    {formatMessage({
                      id: "apps.order-search.button.mark.complete"
                    })}
                  </h3>
                </FormRow>
                <ButtonWrapper>
                  <DefaultButton
                    onClick={() => completeOrder(detailedOrder.id)}
                    style={{
                      backgroundColor: "#00aa10",
                      color: "white",
                      width: "100%",
                      maxWidth: "none"
                    }}
                  >
                    {formatMessage({
                      id: "common.confirm"
                    })}
                  </DefaultButton>
                </ButtonWrapper>
              </ConfirmationBox>
            )}
          </FormContainer>
          <FormContainer>
            {!cancelConfirmationOpen && (
              <ButtonWrapper>
                <DefaultButton
                  onClick={openCancelConfirmation}
                  style={{
                    backgroundColor: "#c20000",
                    color: "white",
                    width: "100%",
                    maxWidth: "500px"
                  }}
                >
                  {formatMessage({
                    id: "apps.order-search.button.mark.incomplete"
                  })}
                </DefaultButton>
              </ButtonWrapper>
            )}
            {cancelConfirmationOpen && (
              <ConfirmationBox>
                <FormRow style={{ display: "flex", justifyContent: "center" }}>
                  <h3>
                    {formatMessage({
                      id: "apps.order-search.button.mark.incomplete"
                    })}
                  </h3>
                </FormRow>
                <ButtonWrapper>
                  <DefaultButton
                    onClick={() => cancelOrder(detailedOrder.id)}
                    style={{
                      backgroundColor: "#c20000",
                      color: "white",
                      width: "100%",
                      maxWidth: "500px"
                    }}
                  >
                    {formatMessage({
                      id: "common.confirm"
                    })}
                  </DefaultButton>
                </ButtonWrapper>
              </ConfirmationBox>
            )}
          </FormContainer>
        </div>
      )}
    </div>
  )
}

export default (injectIntl(observer(OrderHandlePanel)): any)
